<script setup lang="ts">
import useOpenpayCard from "@/composables/useOpenpayCards";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import AlertComponent from "./AlertComponent.vue";

interface Props {
  cardId: string;
  holderName: string;
  brand: string;
  bank?: string;
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  alias?: string;
  ownership?: 'user' | 'company';
  queryKey: [string, number?];
}

const queryClient = useQueryClient();

const props = withDefaults(defineProps<Props>(), {});

const alert = ref(false);

const { deleteCardAsync, deleteCardLoading } = useOpenpayCard({
  params: {
    card_id: props.cardId,
  },
  immediate: false,
});

const actionAlert = () => {
  alert.value = !alert.value;
};

const handleDeleteCard = async (cardId: string) => {
  await deleteCardAsync({
    params: {
      card_id: cardId,
    },
    config: {
      onSuccessToast: "Tarjeta eliminada correctamente",
      onErrorToast: "Hubo un error al eliminar la tarjeta, intente de nuevo",
      raiseToast: true,
    },
  });

  await queryClient.invalidateQueries(props.queryKey);
  actionAlert();
};

const normalizedBrand = computed(() =>
  props.brand?.toLowerCase().replace(/\s/g, '_') || ''
);

const isVisa = computed(() => normalizedBrand.value === 'visa');
const isMastercard = computed(() =>
  normalizedBrand.value === 'mastercard' || normalizedBrand.value === 'master_card'
);
const isAmex = computed(() =>
  normalizedBrand.value === 'american_express' || normalizedBrand.value === 'amex'
);

const ownershipLabel = computed(() => {
  if (props.ownership === "company") return "Corporativa";
  if (props.ownership === "user") return "Personal";
  return "";
});

const ownershipColor = "bg-white/15 text-white border-white/25";

const cardBgColor = computed(() => {
  if (props.ownership === "company") return "bg-emerald-800";
  if (props.ownership === "user") return "bg-blue-ef-400";
  return "bg-slate-800";
});

const displayNumber = computed(() => {
  const raw = props.cardNumber?.replace(/\s/g, '') || '';
  if (raw.length <= 4) return raw.padStart(4, '•');
  return `•••• •••• •••• ${raw.slice(-4)}`;
});
</script>

<template>
  <div
    class="group relative rounded-2xl overflow-hidden shadow-lg transition-transform duration-300 hover:-translate-y-1"
    style="aspect-ratio: 1.586 / 1"
  >
    <!-- Fondo sólido -->
    <div class="absolute inset-0" :class="cardBgColor" />

    <div class="absolute inset-0 p-5 flex flex-col justify-between">
      <!-- Top: alias + delete -->
      <div class="flex justify-between items-start">
        <div class="flex-1 min-w-0">
          <p
            v-if="alias"
            class="text-[11px] font-semibold text-white/90 uppercase tracking-widest truncate mb-0.5"
          >
            {{ alias }}
          </p>
          <p class="text-xs text-white/50 truncate">
            {{ brand }}
          </p>
        </div>

        <div class="flex items-center gap-2">
          <span
            v-if="ownershipLabel"
            class="px-2 py-0.5 rounded-full text-[9px] font-semibold border backdrop-blur-sm"
            :class="ownershipColor"
          >
            {{ ownershipLabel }}
          </span>
          <button
            type="button"
            class="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-black/30 hover:bg-red-500/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            @click="actionAlert()"
            :disabled="deleteCardLoading"
            aria-label="Eliminar tarjeta"
          >
            <span v-if="deleteCardLoading" class="material-symbols-outlined text-white text-[15px] animate-spin">progress_activity</span>
            <span v-else class="material-symbols-outlined text-white text-[15px]">delete</span>
          </button>
        </div>
      </div>

      <!-- Chip -->
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-8 rounded-md bg-amber-300 border border-amber-500/30 flex items-center justify-center overflow-hidden"
        >
          <svg
            class="w-6 h-5 opacity-60"
            viewBox="0 0 24 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="2" y="6" width="20" height="8" rx="1" stroke="#92400e" stroke-width="1.5" />
            <line x1="6" y1="6" x2="6" y2="14" stroke="#92400e" stroke-width="1" />
            <line x1="18" y1="6" x2="18" y2="14" stroke="#92400e" stroke-width="1" />
            <line x1="2" y1="10" x2="22" y2="10" stroke="#92400e" stroke-width="1" />
          </svg>
        </div>
        <!-- Visa -->
        <svg
          v-if="isVisa"
          class="h-6 w-auto drop-shadow-sm"
          viewBox="0 0 24 24"
          fill="white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.112 8.262L5.97 15.758H3.92L2.374 9.775c-.094-.368-.175-.503-.461-.658C1.447 8.864.677 8.627 0 8.479l.046-.217h3.3a.904.904 0 01.894.764l.817 4.338 2.018-5.102zm8.033 5.049c.008-1.979-2.736-2.088-2.717-2.972.006-.269.262-.555.822-.628a3.66 3.66 0 011.913.336l.34-1.59a5.207 5.207 0 00-1.814-.333c-1.917 0-3.266 1.02-3.278 2.479-.012 1.079.963 1.68 1.698 2.04.756.367 1.01.603 1.006.931-.005.504-.602.725-1.16.734-.975.015-1.54-.263-1.992-.473l-.351 1.642c.453.208 1.289.39 2.156.398 2.037 0 3.37-1.006 3.377-2.564m5.061 2.447H24l-1.565-7.496h-1.656a.883.883 0 00-.826.55l-2.909 6.946h2.036l.405-1.12h2.488zm-2.163-2.656l1.02-2.815.588 2.815zm-8.16-4.84l-1.603 7.496H8.34l1.605-7.496z"
          />
        </svg>

        <!-- Mastercard -->
        <svg
          v-else-if="isMastercard"
          class="h-8 w-auto drop-shadow-sm"
          viewBox="0 0 48 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="17" cy="15" r="12" fill="#EB001B" />
          <circle cx="31" cy="15" r="12" fill="#F79E1B" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M24 6.5c2.8 2.2 4.5 5.6 4.5 9.5s-1.7 7.3-4.5 9.5c-2.8-2.2-4.5-5.6-4.5-9.5s1.7-7.3 4.5-9.5z"
            fill="#FF5F00"
          />
        </svg>

        <!-- Amex -->
        <div
          v-else-if="isAmex"
          class="h-6 px-2 rounded-[3px] bg-white flex items-center justify-center"
        >
          <span class="text-[#006FCF] font-black text-[11px] italic tracking-tight">AMEX</span>
        </div>

        <span
          v-else
          class="text-white/70 text-xs font-semibold uppercase tracking-wider"
        >
          {{ brand }}
        </span>
      </div>

      <!-- Bottom: number + expiry + holder -->
      <div>
        <p class="text-white text-lg tracking-[0.15em] font-mono mb-3 drop-shadow-sm">
          {{ displayNumber }}
        </p>
        <div class="flex justify-between items-end">
          <div>
            <p class="text-[9px] text-white/50 uppercase tracking-wider">Titular</p>
            <p class="text-sm font-semibold text-white truncate max-w-[180px]">
              {{ holderName }}
            </p>
          </div>
          <div class="text-right">
            <p class="text-[9px] text-white/50 uppercase tracking-wider">Válida hasta</p>
            <p class="text-sm font-semibold text-white font-mono">
              {{ expirationMonth.padStart(2, '0') }}/{{ expirationYear }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <AlertComponent
    v-model="alert"
    :type="'warning'"
    :dismiss="true"
    :loading="deleteCardLoading"
    :on-confirm="
      () => {
        handleDeleteCard(cardId);
      }
    "
  >
    <template #title>
      {{ "¿Estás seguro de eliminar esta tarjeta?" }}
    </template>

    <template #body>
      {{ "Esta acción no se puede deshacer." }}
    </template>

    <template #closeButton> Cancelar </template>

    <template #button>
      {{ deleteCardLoading ? "Eliminando..." : "Eliminar" }}
    </template>
  </AlertComponent>
</template>
